<template>
    <div v-click-outside="closeAllMenus">
        <p v-if="loading" role="status" aria-live="polite" class="m-0">
            {{ translations.integrations.common.loading[lang] }}...
        </p>
        <p v-if="noResults" role="status" aria-live="polite" class="m-0">
            {{ translations.integrations[integrationSlug].errors['no-results'].message[lang] }}
            {{ translations.integrations[integrationSlug].errors['no-results'].suggestion[lang] }}
        </p>
        <div v-show="!loading && !noResults && !loadingSpeach">
            <audio
                ref="audioPlayer"
                class="hidden"
                preload="metadata"
                autoplay
                @canplay="getAudioDuration"
                @timeupdate="getTimeEnlapsed"
                @ended="closePlayerWhenIsInnactive"
            ></audio>

            <!-- Row 1: Play/Pause + Progress bar + Speed -->
            <div class="flex items-center gap-2">
                <TtsPlayButton :is-playing="isPlaying" @toggle="playOrPauseAudio" />

                <TtsProgressBar
                    :percent="progressPercent"
                    :is-playing="isPlaying"
                    :is-seekable="true"
                    @seek="seekToPercent"
                />

                <TtsSpeedControl
                    :speed="playbackSpeed"
                    :is-menu-visible="isSpeedMenuVisible"
                    :is-mobile="onlyMobileVisible"
                    @change="setPlaybackSpeed"
                    @toggle-menu="toggleSpeedMenu"
                    @close-menu="isSpeedMenuVisible = false"
                />
            </div>

            <!-- Row 2: Voice selector -->
            <div class="relative mt-2 border-t border-gray-200 pt-2 dark:border-gray-700">
                <button
                    class="flex w-full cursor-pointer items-center justify-between border-none bg-transparent p-0 text-[13px] text-theme-text"
                    @click="toggleSelectVoice"
                >
                    <span class="truncate opacity-70">
                        {{ localizations[lang].listening.voice }}:
                        <span class="font-medium opacity-100">{{ selectedVoiceId }}</span>
                    </span>
                    <svg-icon icon="chevron-down" width="16px" height="16px" class="ml-1 shrink-0 opacity-60" />
                </button>
                <ChangeVoiceMenu
                    :is-visible="wantSelectVoice"
                    :voices="supportedVoicesForCurrentLang"
                    :selected-voice="selectedVoiceId"
                    :open-up="onlyMobileVisible"
                    :title="localizations[lang].listening.voice"
                    @select="changeVoice"
                    @close="wantSelectVoice = false"
                />
            </div>
        </div>
        <div role="status" aria-live="polite">
            <LoadingSpinner v-if="loadingSpeach" width="30px" height="30px" not-centered />
        </div>
    </div>
</template>

<script>
'use strict';

import { mapGetters, mapMutations, mapState } from 'vuex';

import supportedVoices from '_shared_/js/integrations/listen-supported-voices.js';
import ContentEncryptionService from '../../../../src/js/components/lib/ContentEncryptionService.js';
import Encrypter from '../../../../src/js/components/lib/Encrypter.js';
import { listenAPI } from '../../../../src/js/communication/integrations';
import { TTS_DEFAULT_PLAYBACK_SPEED, TTS_MAX_CHARACTERS } from '_shared_/js/constants/readerSelectionToolLimits.js';
import ChangeVoiceMenu from './ChangeVoiceMenu.vue';
import LoadingSpinner from '_shared_/UI/Utilities/LoadingSpinner.vue';
import TtsPlayButton from './tts/TtsPlayButton.vue';
import TtsProgressBar from './tts/TtsProgressBar.vue';
import TtsSpeedControl from './tts/TtsSpeedControl.vue';

export default {
    name: 'Listen',
    components: {
        ChangeVoiceMenu,
        LoadingSpinner,
        TtsPlayButton,
        TtsProgressBar,
        TtsSpeedControl,
    },
    props: {
        text: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            endpoint: '',
            noResults: false,
            loading: true,
            cacheSpeech: false,
            isSpeaking: false,
            playlist: [],
            audioElement: null,
            integrationSlug: 'listen',
            selectedVoiceId: '',
            supportedVoices,
            isPlaying: true,
            timeElapsed: 0,
            audioDuration: 0,
            wantSelectVoice: false,
            loadingSpeach: false,
            autoClosed: null,
            fetchingNext: false,
            nextAudioUrl: '',
            playNextAudio: false,
            playbackSpeed: TTS_DEFAULT_PLAYBACK_SPEED,
            isSpeedMenuVisible: false,
            /**
             * Maximum total text length for TTS.
             *
             * Validated in ListenButton.vue before opening the player.
             * This fallback validation ensures the limit is enforced even if called directly.
             *
             * See: _shared_/js/constants/readerSelectionToolLimits.js::TTS_MAX_CHARACTERS
             */
            maxCharacters: TTS_MAX_CHARACTERS,
        };
    },
    computed: {
        ...mapState(['baseEndpoint', 'integrations', 'translations', 'lang', 'localizations', 'ui', 'issue']),
        ...mapGetters(['onlyMobileVisible']),
        supportedVoicesForCurrentLang() {
            const lang = Object.keys(this.supportedVoices).includes(this.issue.lang) ? this.issue.lang : this.lang;
            // Add extra voices for different languages if required
            if (this.ui.extraVoices.chinese) {
                return { ...this.supportedVoices[lang], ...this.supportedVoices['zh'] };
            }

            return this.supportedVoices[lang];
        },
        isPlayingType() {
            return this.ui.listening.texToListen === this.ui.listening.pageToListen ? 'page' : 'highlight';
        },
        progressPercent() {
            if (!this.audioDuration) return 0;
            return Math.min((this.timeElapsed / this.audioDuration) * 100, 100);
        },
    },
    watch: {
        'ui.listening.texToListen'() {
            this.handle(true);
        },
        timeElapsed() {
            //If there is messages in chunks and current audio have only 15 seconds remaing, call amazon api with next message and put it in queue
            if (
                this.audioDuration - this.timeElapsed <= 15 &&
                !this.fetchingNext &&
                !this.nextAudioUrl &&
                this.messages.length > 0
            ) {
                this.fetchingNext = true;
                const message = this.messages[0];
                this.getSpeechFromAWSInBackgroud(message);
            }
        },
    },
    mounted() {
        console.log('debug:components tts - api');

        this.selectedVoiceId =
            localStorage.getItem(`selectedVoiceId:${this.issue.lang}`) ||
            Object.keys(this.supportedVoicesForCurrentLang)[0] ||
            Object.keys(this.supportedVoices.en)[0];
        this.endpoint =
            this.baseEndpoint +
            this.integrations.integrationsBaseEndpoint +
            this.integrations.items[this.integrationSlug].endpoint;
        this.audioElement = this.$refs.audioPlayer;
        this.handle();
    },
    beforeDestroy() {
        if (this.isSpeaking) {
            this.shutUp();
        }
    },
    methods: {
        ...mapMutations(['closeListeningPlayer']),
        async handle(changedVoice = false) {
            this.loading = true;
            this.clearClosePlayer();
            try {
                /**
                 * Fallback validation for text length.
                 * Primary validation occurs in ReaderSelectionTool.vue before opening player.
                 * This ensures the limit is enforced even if component is called directly.
                 */
                if (this.text.length > this.maxCharacters) {
                    this.noResults = true;
                    this.loading = false;
                    console.error(`Text too long for TTS: ${this.text.length} characters (max: ${this.maxCharacters})`);
                    return;
                }

                localStorage.setItem(`selectedVoiceId:${this.issue.lang}`, this.selectedVoiceId);

                if (this.isSpeaking || this.loadingSpeach) {
                    this.shutUp();
                }

                await this.Speak(this.text, changedVoice);
            } catch (e) {
                console.error('error', e);
            }
            this.loading = false;
            this.isPlaying = true;
        },
        Speak(msg, reset = false) {
            //Split large message to chunks and put in messages array, now call amazon tts function with first message of the array.
            //Remaining messages in the array will process later
            this.messages = msg.match(/(.|[\r\n]){1,500}(\.|$|;|:|,)/g);
            msg = this.messages[0];
            this.messages.splice(0, 1);

            if (reset) {
                this.say(msg).then(this.sayNext);
                return;
            }
            if (this.isSpeaking) {
                this.playlist.push(msg);
            } else {
                this.say(msg).then(this.sayNext);
            }
        },

        // Speak & return promise
        SpeakWithPromise(msg) {
            return this.say(msg);
        },

        ForgetCachedSpeec() {
            localStorage.removeItem('chattyKathyDictionary');
        },

        // Quit talking, clear playlist
        shutUp() {
            this.audioElement.currentTime = 0;
            this.audioElement.pause();
            this.playlist = [];
        },

        // Speak the message
        say(message) {
            return new Promise((successCallback, _errorCallback) => {
                this.isSpeaking = true;
                this.getAudio(message).then(this.playAudio).then(successCallback);
            });
        },
        // Say next
        sayNext() {
            const list = this.playlist;
            if (list.length > 0) {
                const msg = list[0];
                list.splice(0, 1);
                this.say(msg).then(this.sayNext);
            }
        },

        // Get Audio
        getAudio(message) {
            return this.requestSpeechFromAWS(message);
        },

        // Make request to Amazon polly
        async requestSpeechFromAWS(message) {
            this.loadingSpeach = true;
            try {
                const blob = await listenAPI.synthesizeSpeech({
                    text: ContentEncryptionService.shouldEncrypt() ? await Encrypter.encryptText(message) : message,
                    voiceId: this.selectedVoiceId,
                });

                if (!this.ui.listening.opened) {
                    this.shutUp();
                    return blob;
                }

                this.audioElement.src = URL.createObjectURL(blob);
                this.loadingSpeach = false;
                return blob;
            } catch (error) {
                console.error('error', error);
                this.loadingSpeach = false;
                throw error;
            }
        },
        async getSpeechFromAWSInBackgroud(message) {
            try {
                const blob = await listenAPI.synthesizeSpeech({
                    text: ContentEncryptionService.shouldEncrypt() ? await Encrypter.encryptText(message) : message,
                    voiceId: this.selectedVoiceId,
                });

                if (this.playNextAudio) {
                    this.audioElement.src = URL.createObjectURL(blob);
                    this.loadingSpeach = false;
                    this.playNextAudio = false;
                } else if (this.isPlaying) {
                    this.nextAudioUrl = URL.createObjectURL(blob);
                }

                this.fetchingNext = false;
                this.messages.splice(0, 1);
                return blob;
            } catch (error) {
                console.error('error', error);
                this.fetchingNext = false;
                throw error;
            }
        },
        playOrPauseAudio() {
            if (this.isPlaying) {
                this.isPlaying = false;
                this.audioElement.pause();
            } else {
                this.isPlaying = true;
                this.audioElement.play();
                this.clearClosePlayer();
            }
        },
        stop() {
            this.audioElement.pause();
            this.audioElement.currentTime = 0;
            this.isPlaying = false;
            this.playNextAudio = false;
            this.closeListeningPlayer();
        },
        getAudioDuration() {
            this.audioDuration = parseInt(this.audioElement.duration);
            this.audioElement.playbackRate = this.playbackSpeed;
        },
        getTimeEnlapsed() {
            this.timeElapsed = parseInt(this.audioElement.currentTime);
        },
        changeVoice(voice) {
            this.selectedVoiceId = voice;
            this.handle(true);
            this.toggleSelectVoice();
        },
        toggleSelectVoice() {
            this.wantSelectVoice = !this.wantSelectVoice;
            this.isSpeedMenuVisible = false;
        },
        /**
         * Closes all dropdown menus (voice and speed).
         */
        closeAllMenus() {
            this.wantSelectVoice = false;
            this.isSpeedMenuVisible = false;
        },
        /**
         * Toggles the speed selection menu visibility.
         */
        toggleSpeedMenu() {
            this.isSpeedMenuVisible = !this.isSpeedMenuVisible;
            this.wantSelectVoice = false;
        },
        /**
         * Sets the playback speed and applies it to the audio element.
         * @param {number} speed - Speed value between 0.5 and 2
         */
        setPlaybackSpeed(speed) {
            this.playbackSpeed = speed;
            this.isSpeedMenuVisible = false;
            if (this.audioElement) {
                this.audioElement.playbackRate = speed;
            }
        },
        /**
         * Seeks to a position based on percentage (0-1).
         * @param {number} percent - Click position as percentage (0-1)
         */
        seekToPercent(percent) {
            if (!this.audioDuration || !this.audioElement) return;
            this.audioElement.currentTime = percent * this.audioDuration;
        },
        closePlayerWhenIsInnactive() {
            if (this.nextAudioUrl) {
                //If nextAudioUrl has value, Play it
                this.audioElement.src = this.nextAudioUrl;
                this.nextAudioUrl = '';
                return false;
            } else if (this.fetchingNext) {
                //If fetchingNext is true, means waiting for amazon api response, show loading icon and play right after response received
                this.loadingSpeach = true;
                this.playNextAudio = true;
                return;
            }
            this.isPlaying = false;
            this.autoClosed = setTimeout(() => {
                this.stop();
            }, 5000);
        },
        clearClosePlayer() {
            if (this.autoClosed) {
                clearTimeout(this.autoClosed);
            }
        },
    },
};
</script>
